import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import useDirtyGuard, { confirmarSiSucio } from '../hooks/useDirtyGuard'
import { crearPlanilla, CSS_PLANILLA } from '../utils/planilla'
import { colors, font, text, radius } from '../design/tokens'

// STOCK → Artículos en modo planilla (Martin, 6 oct 2026, prototipo aprobado:
// "está muy bien, subilo"). Se edita como en Excel. Las columnas de Presea
// (🔒) se ven y se filtran pero no se editan acá: el sync las pisaría. Las de
// LGR se escriben y se guardan por los MISMOS endpoints del listado (filtros
// de regalo, visibilidad y stock de la web), así cada regla vive en un solo lado.

const fmt$ = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const POR_PAGINA = 500
const MAX_FILAS = 3000

const siNo = v => (v === true ? 'Sí' : v === false ? 'No' : '')
const fecha = v => (v ? String(v).slice(8, 10) + '/' + String(v).slice(5, 7) + '/' + String(v).slice(0, 4) : '')
const VISIBILIDAD = { 1: 'Solo por link directo', 2: 'Listado', 3: 'Búsqueda', 4: 'Listado + búsqueda' }

// `filtro`: 'facetas' = lista de valores del servidor · 'texto' = contiene · 'sino' = Sí/No · null = solo ordenar.
const COLS = [
  { k: 'codigo_alfa', l: 'Código', w: 100, ro: true, mono: true, fz: true, filtro: 'texto' },
  { k: 'descripcion', l: 'Descripción', w: 300, ro: true, fz: true, filtro: 'texto' },
  { k: 'web_tipo_regalo', l: 'Tipo de regalo', w: 150, ed: 'texto', filtro: 'facetas' },
  { k: 'web_filtro_regalo', l: 'Filtro de regalos', w: 170, ed: 'texto', filtro: 'facetas' },
  { k: 'visible', l: 'Disponible', w: 96, ed: 'bool', si: 'Visible', no: 'No visible', filtro: 'sino', param: 'disponible' },
  { k: 'stock', l: 'Stock', w: 90, ed: 'bool', si: 'En stock', no: 'Sin stock', filtro: 'sino', param: 'stock' },
  { k: 'precio_lista', l: 'Precio LISTA1', w: 120, ro: true, num: true, fmt: v => (v == null || v === '' ? '' : '$ ' + fmt$.format(Number(v))) },
  { k: 'codigo_presea', l: 'Cód. Presea', w: 92, ro: true, mono: true, filtro: 'texto' },
  { k: 'rubro', l: 'Rubro', w: 120, ro: true, filtro: 'facetas' },
  { k: 'subrubro', l: 'Subrubro', w: 120, ro: true, filtro: 'facetas' },
  { k: 'tipo_prod', l: 'Tipo', w: 140, ro: true, filtro: 'facetas' },
  { k: 'familia', l: 'Familia', w: 140, ro: true, filtro: 'facetas' },
  { k: 'marca', l: 'Marca', w: 120, ro: true, filtro: 'facetas' },
  { k: 'bodega', l: 'Bodega', w: 120, ro: true, filtro: 'facetas' },
  { k: 'contenido', l: 'Cont.', w: 70, ro: true, num: true, filtro: 'facetas' },
  { k: 'pais', l: 'País', w: 90, ro: true, filtro: 'facetas' },
  // El resto del maestro (Martin, 6 oct 2026: "la planilla tampoco baja todos los campos").
  { k: 'cepa', l: 'Cepa', w: 120, ro: true, filtro: 'facetas' },
  { k: 'anio', l: 'Año', w: 64, ro: true, num: true, filtro: 'facetas' },
  { k: 'modelo', l: 'Modelo', w: 120, ro: true, filtro: 'texto' },
  { k: 'presenta', l: 'Presenta', w: 100, ro: true, filtro: 'facetas' },
  { k: 'bultos', l: 'Bultos', w: 64, ro: true, num: true },
  { k: 'deta_lista', l: 'Descripción web', w: 260, ro: true, filtro: 'texto' },
  { k: 'codigo_ean', l: 'EAN', w: 130, ro: true, mono: true, filtro: 'texto' },
  { k: 'proveedor', l: 'Cód. proveedor', w: 100, ro: true, mono: true, filtro: 'texto' },
  { k: 'nomprov', l: 'Proveedor', w: 170, ro: true, filtro: 'facetas' },
  { k: 'codigo_pro', l: 'Cód. pro', w: 90, ro: true, mono: true },
  { k: 'nacional', l: 'Nacional', w: 70, ro: true },
  { k: 'cod_alfab1', l: 'SKU actual (VG)', w: 110, ro: true, mono: true },
  { k: 'perfil', l: 'Perfil', w: 90, ro: true },
  { k: 'iva', l: 'IVA %', w: 64, ro: true, num: true },
  { k: 'internos', l: 'Internos %', w: 80, ro: true, num: true },
  { k: 'percep_iva', l: 'Percep. IVA', w: 86, ro: true, num: true },
  { k: 'costo', l: 'Costo', w: 110, ro: true, num: true, fmt: v => (v == null || v === '' ? '' : '$ ' + fmt$.format(Number(v))) },
  { k: 'cpa', l: 'CPA', w: 90, ro: true, num: true },
  { k: 'dtoligierc', l: 'Dto. Ligier', w: 80, ro: true, num: true },
  { k: 'activo', l: 'Activo', w: 64, ro: true, fmt: siNo },
  { k: 'factura_como_codigo', l: 'Factura como', w: 110, ro: true, mono: true, nota: 'Se cambia en la ficha del artículo' },
  { k: 'web_visibilidad', l: 'Visibilidad web', w: 140, ro: true, nota: 'La calcula LGR', fmt: v => VISIBILIDAD[v] || '' },
  { k: 'web_solo_qr', l: 'Solo por QR', w: 84, ro: true, nota: 'La calcula LGR', fmt: siNo },
  { k: 'web_publicado_lgr', l: 'Publicado por LGR', w: 120, ro: true, nota: 'La calcula LGR', fmt: siNo },
  { k: 'disponible_por_ingreso', l: 'Entró mercadería', w: 110, ro: true, nota: 'La calcula LGR', fmt: siNo },
  { k: 'disponible_ingreso_cant', l: 'Cant. ingresada', w: 100, ro: true, num: true, nota: 'La calcula LGR' },
  { k: 'disponible_ingreso_at', l: 'Fecha ingreso', w: 96, ro: true, nota: 'La calcula LGR', fmt: fecha },
  { k: 'sin_stock_at', l: 'Sin stock desde', w: 104, ro: true, nota: 'La calcula LGR', fmt: fecha },
  { k: 'web_en_stock_at', l: 'Stock tocado', w: 96, ro: true, nota: 'La calcula LGR', fmt: fecha },
  { k: 'magento_qty', l: 'Cant. Magento', w: 96, ro: true, num: true, nota: 'Último dato de Magento' },
  { k: 'magento_sync_at', l: 'Sync Magento', w: 96, ro: true, nota: 'Último dato de Magento', fmt: fecha },
  { k: 'sync_presea_at', l: 'Sync Presea', w: 96, ro: true, fmt: fecha },
  { k: 'created_at', l: 'Alta en LGR', w: 96, ro: true, nota: 'La calcula LGR', fmt: fecha },
  { k: 'updated_at', l: 'Últ. cambio', w: 96, ro: true, nota: 'La calcula LGR', fmt: fecha },
  { k: 'id', l: 'ID LGR', w: 70, ro: true, num: true, nota: 'La calcula LGR' },
]

const visibleDe = a => a.web_habilitado === true && [2, 4].includes(Number(a.web_visibilidad))
const stockDe = a => a.web_en_stock === true && !a.sin_stock

export default function ArticulosPlanillaPage() {
  const navigate = useNavigate()
  const hojaRef = useRef(null)
  const grilla = useRef(null)
  const [q, setQ] = useState('')
  const [f, setF] = useState({})            // filtros de columna del servidor: { campo: [valores] | 'texto' }
  const [sino, setSino] = useState({})      // { disponible: 'si'|'no', stock: 'si'|'no' }
  const [total, setTotal] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [estado, setEstado] = useState({ celdas: 0, prods: 0, deshacer: 0, filas: 0, ref: 'A1', valor: '', ro: true, seleccion: null })
  const [pop, setPop] = useState(null)
  const [confirmar, setConfirmar] = useState(null)   // lista de cambios a guardar
  const [guardando, setGuardando] = useState(false)
  const [toast, setToast] = useState(null)
  const toastRef = useRef(null)
  const filtrosRef = useRef({ f: {}, sino: {} })
  useEffect(() => { filtrosRef.current = { f, sino }; grilla.current?.render() }, [f, sino])

  useDirtyGuard(estado.celdas > 0, 'Tenés cambios sin guardar en la planilla. ¿Salir igual?')

  const avisar = useCallback((m) => { setToast(m); clearTimeout(toastRef.current); toastRef.current = setTimeout(() => setToast(null), 2800) }, [])

  // Monta la grilla una sola vez.
  useEffect(() => {
    grilla.current = crearPlanilla(hojaRef.current, COLS, {
      cambio: setEstado,
      aviso: avisar,
      filtroActivo: j => { const c = COLS[j]; const { f: ff, sino: ss } = filtrosRef.current; return c.param ? !!ss[c.param] : ff[c.k] != null },
      filtro: (j, rect) => setPop({ j, x: Math.max(16, Math.min(rect.left, window.innerWidth - 300)), y: Math.min(rect.bottom + 4, window.innerHeight - 420) }),
    })
    return () => grilla.current?.destruir()
  }, [avisar])

  const cargar = useCallback(async () => {
    setCargando(true); setError('')
    try {
      const base = { per_page: POR_PAGINA, precio_lista: 'LISTA1', ...(q.trim() ? { q: q.trim() } : {}), ...(Object.keys(f).length ? { f } : {}), ...sino }
      let filas = [], pagina = 1, tot = 0, ultima = 1
      do {
        const { data } = await api.get('/articulos', { params: { ...base, page: pagina } })
        filas = filas.concat(data.data || [])
        tot = data.total ?? filas.length
        ultima = data.last_page ?? 1
        pagina++
      } while (pagina <= ultima && filas.length < MAX_FILAS)
      setTotal(tot)
      grilla.current.setFilas(filas.map(a => ({ ...a, visible: visibleDe(a), stock: stockDe(a), factura_como_codigo: a.factura_como?.codigo_alfa ?? '', web_tipo_regalo: a.web_tipo_regalo ?? '', web_filtro_regalo: a.web_filtro_regalo ?? '' })))
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudieron traer los artículos.')
    } finally { setCargando(false) }
  }, [q, f, sino])

  useEffect(() => { const t = setTimeout(cargar, q ? 350 : 0); return () => clearTimeout(t) }, [cargar, q])

  // Cambiar filtros recarga del servidor: antes, no perder lo que no se guardó.
  const conCuidado = (fn) => { if (estado.celdas && !window.confirm('Tenés cambios sin guardar. Si cambiás el filtro se pierden. ¿Seguir?')) return; fn() }

  /** Baja a CSV lo que se está viendo (mismos filtros) con TODOS los campos del maestro. */
  async function exportar() {
    try {
      const params = { ...(q.trim() ? { q: q.trim() } : {}), ...(Object.keys(f).length ? { f } : {}), ...sino }
      const res = await api.get('/articulos/export', { params, responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url; a.download = 'articulos_' + new Date().toISOString().slice(0, 10) + '.csv'; a.click()
      URL.revokeObjectURL(url)
    } catch { avisar('No se pudo exportar.') }
  }

  async function guardar(cambios) {
    setGuardando(true)
    try {
      const grupos = new Map()
      for (const x of cambios) {
        const clave = x.k === 'visible' ? `vis|${x.ahora}` : x.k === 'stock' ? `stk|${x.ahora}` : `txt|${x.k}|${x.ahora}`
        if (!grupos.has(clave)) grupos.set(clave, { ...x, ids: [] })
        grupos.get(clave).ids.push(x.fila.id)
      }
      for (const g of grupos.values()) {
        if (g.k === 'visible') await api.post('/articulos/visibilidad-web', { ids: g.ids, visible: g.ahora })
        else if (g.k === 'stock') await api.post('/articulos/stock-web', { ids: g.ids, en_stock: g.ahora })
        else await api.post('/articulos/filtros-regalo', { ids: g.ids, campo: g.k, valor: g.ahora || null })
      }
      grilla.current.confirmar()
      setConfirmar(null)
      avisar(`${cambios.length} ${cambios.length === 1 ? 'cambio guardado' : 'cambios guardados'}. La tienda los muestra en uno o dos minutos.`)
      cargar()
    } catch (e) {
      avisar(e.response?.data?.message || 'No se pudo guardar. No se perdió nada: probá de nuevo.')
    } finally { setGuardando(false) }
  }

  const colPop = pop ? COLS[pop.j] : null
  return (
    <div style={st.pagina}>
      <style>{CSS_PLANILLA}</style>
      <div style={st.top}>
        <div>
          <div style={st.eyebrow}>STOCK · ARTÍCULOS</div>
          <h1 style={st.h1}>Artículos en modo planilla</h1>
          <p style={st.sub}>Se edita como en Excel. Las columnas azules se escriben acá; las grises (🔒) vienen de Presea o las calcula LGR, y la barra de arriba dice de dónde.</p>
        </div>
        <div style={st.tools}>
          <input style={st.search} type="search" placeholder="Buscar código o descripción" value={q} aria-label="Buscar"
            onChange={e => { const v = e.target.value; conCuidado(() => setQ(v)) }} />
          <Boton sm onClick={() => conCuidado(() => { setF({}); setSino({}); setQ(''); grilla.current.ordenar(null) })}>Quitar filtros</Boton>
          <Boton sm disabled={!estado.deshacer} onClick={() => grilla.current.deshacer()}>↶ Deshacer</Boton>
          <Boton sm disabled={!estado.celdas} onClick={() => grilla.current.descartar()}>Descartar cambios</Boton>
          <Boton sm variante="primary" disabled={!estado.celdas} onClick={() => setConfirmar(grilla.current.cambios())}>Guardar cambios</Boton>
          <Boton sm onClick={exportar}>Exportar</Boton>
          <Boton sm variante="ghost" onClick={() => { if (confirmarSiSucio()) navigate('/articulos') }}>← Listado</Boton>
        </div>
      </div>

      <div style={st.formula} aria-live="polite">
        <div style={st.ref}>{estado.ref}</div>
        <div style={st.fx}>fx</div>
        <div style={st.val}>{estado.valor}</div>
        <div style={st.lock}>{estado.ro ? '🔒 ' + (estado.nota || 'Viene de Presea') : 'Se edita en LGR'}</div>
      </div>
      {error && <div style={st.error}>{error}</div>}

      <div ref={hojaRef} style={st.hoja} tabIndex={0} aria-label="Planilla de artículos" />

      <div style={st.estado}>
        <span>{cargando ? 'Trayendo artículos…' : `${estado.filas.toLocaleString('es-AR')} de ${(total ?? 0).toLocaleString('es-AR')} artículos`}
          {total > estado.filas && !cargando ? ` · se muestran los primeros ${MAX_FILAS.toLocaleString('es-AR')}: filtrá para ver el resto` : ''}</span>
        {estado.celdas > 0 && <span style={st.cambios}>{estado.celdas} {estado.celdas === 1 ? 'celda cambiada' : 'celdas cambiadas'} en {estado.prods} {estado.prods === 1 ? 'producto' : 'productos'} · sin guardar</span>}
        <span style={st.atajos}>↑↓←→ moverse · Shift o arrastrar para elegir · escribir o F2 edita · Supr borra · ⌘C / ⌘V copiar y pegar (también desde Excel) · ⌘D rellenar hacia abajo · ⌘Z deshacer</span>
        {estado.seleccion && <span style={st.der}>Recuento: {estado.seleccion.n}{estado.seleccion.suma != null ? ` · Suma: $ ${fmt$.format(estado.seleccion.suma)}` : ''}</span>}
      </div>

      {pop && (
        <PopFiltro col={colPop} x={pop.x} y={pop.y} q={q} f={f} sino={sino}
          onCerrar={() => setPop(null)}
          onOrden={(dir) => { grilla.current.ordenar(colPop.k, dir); setPop(null) }}
          onAplicar={(valor) => {
            setPop(null)
            conCuidado(() => {
              if (colPop.param) setSino(s => { const n = { ...s }; if (valor) n[colPop.param] = valor; else delete n[colPop.param]; return n })
              else setF(prev => { const n = { ...prev }; if (valor == null || (Array.isArray(valor) && !valor.length) || valor === '') delete n[colPop.k]; else n[colPop.k] = valor; return n })
            })
          }} />
      )}

      {confirmar && (
        <Modal title={`Guardar ${confirmar.length} ${confirmar.length === 1 ? 'cambio' : 'cambios'}`} onClose={() => !guardando && setConfirmar(null)} maxWidth="760px">
          <p style={{ ...st.sub, marginTop: 0 }}>Se graban en el maestro de artículos y la tienda los muestra en uno o dos minutos.</p>
          <div style={st.tablaCambios}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr>{['Código', 'Producto', 'Campo', 'Antes', 'Ahora'].map(h => <th key={h} style={st.thC}>{h}</th>)}</tr></thead>
              <tbody>
                {confirmar.map((x, i) => {
                  const c = COLS.find(cc => cc.k === x.k)
                  const t = v => (c.ed === 'bool' ? (v ? c.si : c.no) : (v || '—'))
                  return <tr key={i}><td style={{ ...st.tdC, fontFamily: font.mono }}>{x.fila.codigo_alfa}</td><td style={st.tdC}>{x.fila.descripcion}</td><td style={st.tdC}>{c.l}</td><td style={{ ...st.tdC, color: colors.textSec }}>{t(x.antes)}</td><td style={{ ...st.tdC, fontWeight: 600 }}>{t(x.ahora)}</td></tr>
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 12 }}>
            <Boton variante="ghost" disabled={guardando} onClick={() => setConfirmar(null)}>Cancelar</Boton>
            <Boton variante="primary" disabled={guardando} onClick={() => guardar(confirmar)}>{guardando ? 'Guardando…' : 'Guardar'}</Boton>
          </div>
        </Modal>
      )}
      {toast && <div style={st.toast} role="status">{toast}</div>}
    </div>
  )
}

/** Filtro de una columna: valores del servidor (dependientes de los demás filtros), texto o Sí/No, y orden. */
function PopFiltro({ col, x, y, q, f, sino, onCerrar, onOrden, onAplicar }) {
  const [valores, setValores] = useState(null)
  const [elegidos, setElegidos] = useState(() => new Set(Array.isArray(f[col.k]) ? f[col.k] : []))
  const [buscar, setBuscar] = useState('')
  const [txt, setTxt] = useState(typeof f[col.k] === 'string' ? f[col.k] : '')
  const ref = useRef(null)

  useEffect(() => {
    if (col.filtro !== 'facetas') return
    const otros = Object.fromEntries(Object.entries(f).filter(([k]) => k !== col.k))
    api.get('/articulos/facetas', { params: { campo: col.k, ...(q ? { q } : {}), ...(Object.keys(otros).length ? { f: otros } : {}) } })
      .then(({ data }) => setValores(data.map(String)))
      .catch(() => setValores([]))
  }, [col, q, f])
  useEffect(() => {
    const fuera = e => { if (ref.current && !ref.current.contains(e.target)) onCerrar() }
    const esc = e => { if (e.key === 'Escape') onCerrar() }
    setTimeout(() => document.addEventListener('mousedown', fuera), 0)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc) }
  }, [onCerrar])

  const visibles = (valores || []).filter(v => !buscar || v.toLowerCase().includes(buscar.toLowerCase()))
  return (
    <div ref={ref} style={{ ...st.pop, left: x, top: y }}>
      <div style={st.popTit}>{col.l}</div>
      <button type="button" style={st.op} onClick={() => onOrden(1)}>Ordenar A → Z</button>
      <button type="button" style={st.op} onClick={() => onOrden(-1)}>Ordenar Z → A</button>
      {col.filtro === 'texto' && (
        <form onSubmit={e => { e.preventDefault(); onAplicar(txt.trim()) }} style={{ display: 'grid', gap: 6, padding: '6px 4px' }}>
          <input style={st.search} autoFocus placeholder="Contiene…" value={txt} onChange={e => setTxt(e.target.value)} aria-label="Contiene" />
          <div style={st.popPie}><Boton sm variante="ghost" onClick={() => onAplicar('')}>Quitar filtro</Boton><Boton sm variante="primary" type="submit">Aplicar</Boton></div>
        </form>
      )}
      {col.filtro === 'sino' && (
        <div style={{ display: 'grid', gap: 2, padding: '6px 0' }}>
          {[['', 'Todos'], ['si', col.si], ['no', col.no]].map(([v, t]) => (
            <button key={v} type="button" style={{ ...st.op, ...((sino[col.param] || '') === v ? { color: colors.link, fontWeight: 600 } : null) }} onClick={() => onAplicar(v)}>{t}</button>
          ))}
        </div>
      )}
      {col.filtro === 'facetas' && (
        <>
          <input style={{ ...st.search, width: '100%', boxSizing: 'border-box', margin: '6px 0 4px' }} placeholder="Buscar" value={buscar} onChange={e => setBuscar(e.target.value)} aria-label="Buscar valor" />
          <div style={st.lista}>
            {valores == null ? <div style={st.nota}>Cargando…</div> : visibles.length === 0 ? <div style={st.nota}>Sin valores</div> : visibles.map(v => (
              <label key={v} style={st.ck}><input type="checkbox" checked={elegidos.has(v)} onChange={() => setElegidos(s => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n })} /> {v}</label>
            ))}
          </div>
          <div style={st.popPie}>
            <Boton sm variante="ghost" onClick={() => onAplicar(null)}>Quitar filtro</Boton>
            <Boton sm variante="primary" onClick={() => onAplicar([...elegidos])}>Aplicar</Boton>
          </div>
        </>
      )}
      {!col.filtro && <div style={st.nota}>Esta columna solo se ordena.</div>}
    </div>
  )
}

const st = {
  pagina: { display: 'flex', flexDirection: 'column', gap: 8, height: 'calc(100vh - 32px)', padding: '12px 16px', boxSizing: 'border-box', fontFamily: font.sans, color: colors.text },
  top: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '8px 16px' },
  eyebrow: { fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase', color: colors.textMeta },
  h1: { margin: '2px 0 0', fontSize: 19, fontWeight: 650 },
  sub: { margin: '2px 0 0', fontSize: 12, color: colors.textSec, maxWidth: 760 },
  tools: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' },
  search: { fontSize: 12.5, border: '1px solid #c5ccd6', borderRadius: radius.sm, padding: '5px 8px', width: 220, maxWidth: '100%', color: colors.text, background: colors.bg },
  formula: { display: 'flex', alignItems: 'stretch', border: '1px solid #c5ccd6', borderRadius: 4, overflow: 'hidden', minWidth: 0 },
  ref: { fontFamily: font.mono, fontSize: 12, padding: '5px 10px', background: '#eef2f7', borderRight: '1px solid #c5ccd6', color: colors.textSec, minWidth: 70 },
  fx: { fontSize: 12, color: colors.textMeta, padding: '5px 8px', borderRight: '1px solid #dfe4ea', fontStyle: 'italic' },
  val: { flex: 1, padding: '5px 8px', fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 },
  lock: { color: colors.textMeta, fontSize: 11.5, padding: '5px 8px', whiteSpace: 'nowrap' },
  hoja: { flex: 1, minHeight: 240, overflow: 'auto', border: '1px solid #c5ccd6', borderRadius: 4, position: 'relative', outline: 'none', background: colors.bg },
  estado: { display: 'flex', flexWrap: 'wrap', gap: '4px 18px', alignItems: 'center', fontSize: 12, color: colors.textSec },
  cambios: { color: '#b45309', fontWeight: 600 },
  atajos: { fontSize: 11.5, color: colors.textMeta },
  der: { marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' },
  error: { color: colors.err, background: colors.errBg, borderRadius: radius.sm, padding: '6px 10px', fontSize: text.sec },
  pop: { position: 'fixed', zIndex: 900, background: colors.bg, border: '1px solid #c5ccd6', borderRadius: radius.sm, boxShadow: '0 12px 32px rgba(15, 23, 42, .22)', padding: 6, width: 280, fontSize: 12.5 },
  popTit: { fontWeight: 650, padding: '4px 6px 6px', fontSize: 13 },
  op: { display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'none', padding: '5px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12.5, color: colors.text },
  lista: { maxHeight: 240, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 4, padding: 2 },
  ck: { display: 'flex', gap: 6, alignItems: 'center', padding: '2px 6px' },
  popPie: { display: 'flex', justifyContent: 'space-between', gap: 6, paddingTop: 6 },
  nota: { color: colors.textMeta, fontSize: 12, padding: '6px 8px' },
  tablaCambios: { maxHeight: '55vh', overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 4 },
  thC: { position: 'sticky', top: 0, background: '#eef2f7', textAlign: 'left', padding: '4px 8px', fontSize: 12, borderBottom: '1px solid #dfe4ea' },
  tdC: { padding: '3px 8px', borderBottom: '1px solid #f1f5f9' },
  toast: { position: 'fixed', left: '50%', bottom: 18, transform: 'translateX(-50%)', background: colors.text, color: '#fff', padding: '8px 14px', borderRadius: radius.sm, fontSize: text.sec, zIndex: 1000 },
}
